import { useRef, useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/SEO';

const values = [
  {
    icon: 'ri-leaf-fill',
    title: 'Purity in Every Step',
    description: 'From sourcing to final preparation, we ensure no chemical treatment or artificial intervention ever touches our products.',
  },
  {
    icon: 'ri-hand-heart-fill',
    title: 'Made by Human Hands',
    description: 'Real people, real effort — every product is crafted by skilled artisans who take genuine pride in what they create.',
  },
  {
    icon: 'ri-history-line',
    title: 'Rooted in Tradition',
    description: 'Our methods are centuries old. We reject modern shortcuts because we believe the old ways produced the best results.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Health is Our Mission',
    description: 'We exist to support your wellbeing. Every product is chosen and crafted with genuine health benefits in mind.',
  },
];

const process = [
  {
    step: '01',
    title: 'Careful Sourcing',
    description: 'We select only the finest organic ingredients from trusted growers — clean, unprocessed, straight from nature.',
    icon: 'ri-seedling-line',
  },
  {
    step: '02',
    title: 'Hand Cleaning & Preparation',
    description: 'Each ingredient is hand-sorted, cleaned and inspected to ensure absolute purity before processing begins.',
    icon: 'ri-hand-sanitizer-line',
  },
  {
    step: '03',
    title: 'Stone Grinding & Crushing',
    description: 'Using traditional stone grinders and hand-pressing techniques, we preserve the natural oils and nutrients fully.',
    icon: 'ri-hammer-line',
  },
  {
    step: '04',
    title: 'Careful Packaging',
    description: 'Products are packed immediately after preparation to lock in freshness, flavour and nutritional value.',
    icon: 'ri-gift-2-line',
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pearlfoods.pk';

const AboutPage = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Pearl Foods',
      description: 'Pearl Foods is a handmade organic food brand from Islamabad offering pure, non-processed food products.',
      url: `${siteUrl}/about`,
      mainEntity: {
        '@type': 'Organization',
        name: 'Pearl Foods',
        url: siteUrl,
        logo: `${siteUrl}/logo.png`,
        description: '100% organic handmade non-processed food products from Islamabad, Pakistan',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Islamabad',
          addressCountry: 'PK',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+923445299984',
          contactType: 'customer service',
          availableLanguage: ['English', 'Urdu'],
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Pearl Foods',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      description: '100% organic handmade non-processed food products from Islamabad, Pakistan',
      sameAs: ['https://wa.me/923445299984'],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Islamabad',
        addressCountry: 'PK',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+923445299984',
        contactType: 'customer service',
        availableLanguage: ['English', 'Urdu'],
      },
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.scroll-animate').forEach((child) => {
              child.classList.add('visible');
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    const sections = el.querySelectorAll('section');
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('message', formData.message);
      const res = await fetch('https://readdy.ai/api/form/d7n2u8lnkhlbktri3cpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        setError('Something went wrong. Please try WhatsApp instead.');
      }
    } catch {
      setError('Something went wrong. Please try WhatsApp instead.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={ref}>
      <SEO
        title="About Us | Pearl Foods Islamabad"
        description="Learn about Pearl Foods - our story, handmade process, and commitment to 100% organic, non-processed food products crafted with traditional methods in Islamabad."
        keywords="Pearl Foods, organic food Islamabad, handmade food products, traditional food preparation, pure organic ingredients"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 overflow-hidden">
        <div className="absolute inset-0 h-[500px]">
          <img
            src="https://readdy.ai/api/search-image?query=traditional%20handmade%20food%20preparation%20mortar%20pestle%20grinding%20herbs%20spices%20organic%20ingredients%20natural%20light%20rustic%20authentic%20kitchen%20warm%20golden%20brown%20tones%20artisan%20process%20beautiful%20earthy&width=1400&height=700&seq=pf4001&orientation=landscape"
            alt="Pearl Foods handmade process"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-brown-dark/60 to-brand-cream"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-40 text-center">
          <div className="scroll-animate">
            <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-gold font-medium mb-4">
              Our Story
            </span>
            <h1 className="font-serif text-4xl md:text-6xl text-white font-bold mb-4 drop-shadow-sm">
              Born from a Belief<br />in Pure Living
            </h1>
            <p className="font-sans text-white/80 text-base md:text-lg max-w-2xl mx-auto">
              Pearl Foods began with one simple idea: that real food should be made by real hands, with real ingredients, and nothing else.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-brand-cream">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="scroll-animate">
            <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-4">
              The Pearl Foods Story
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold mb-6 leading-tight">
              Handcrafted with Purpose,<br />Trusted with Health
            </h2>
            <div className="space-y-4 font-sans text-brand-brown/80 text-sm md:text-base leading-relaxed">
              <p>
                Pearl Foods was founded in the heart of Islamabad with a mission rooted in tradition and driven by genuine care for people's health. In a world increasingly dominated by processed, chemical-laden foods, we took a different path.
              </p>
              <p>
                Every product we make is a <strong className="text-brand-brown-dark">return to the pure, organic way</strong> that our grandparents lived — hand-ground spices, cold-pressed oils, stone-milled flours. No shortcuts, no machines, no compromises.
              </p>
              <p>
                We believe that the food we eat shapes not just our bodies, but our connection to the earth. Pearl Foods is our way of keeping that connection alive — one handcrafted product at a time.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-6">
              {[
                { label: 'Pure Organic Ingredients', icon: 'ri-leaf-line' },
                { label: 'Zero Machine Processing', icon: 'ri-tools-line' },
                { label: 'Chemical Free Always', icon: 'ri-shield-check-line' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-brand-olive text-sm font-sans">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="scroll-animate scroll-animate-delay-1 grid grid-cols-2 gap-4">
            <div className="rounded-2xl overflow-hidden h-52 col-span-2">
              <img
                src="https://readdy.ai/api/search-image?query=hands%20carefully%20grinding%20organic%20spices%20herbs%20in%20stone%20mortar%20pestle%20close%20up%20detail%20natural%20handmade%20artisan%20process%20warm%20golden%20light%20premium%20photography%20earthy%20tones%20beautiful%20authentic&width=800&height=400&seq=pf4002&orientation=landscape"
                alt="Handmade process"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-2xl overflow-hidden h-36">
              <img
                src="https://readdy.ai/api/search-image?query=organic%20natural%20ingredients%20herbs%20spices%20seeds%20arranged%20on%20rustic%20wooden%20table%20traditional%20food%20preparation%20natural%20lighting%20earthy%20warm%20tones%20flat%20lay%20beautiful%20premium&width=400&height=300&seq=pf4003&orientation=landscape"
                alt="Organic ingredients"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-2xl overflow-hidden h-36 bg-brand-olive/10 flex flex-col items-center justify-center text-center p-4">
              <p className="font-serif text-3xl text-brand-olive font-bold">100%</p>
              <p className="font-sans text-brand-brown text-xs mt-1">Organic & Pure</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-brand-beige">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 scroll-animate">
            <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-3">
              How It&apos;s Made
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold mb-4">
              Our Handmade Process
            </h2>
            <div className="w-16 h-0.5 bg-brand-gold mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <div
                key={step.step}
                className={`scroll-animate scroll-animate-delay-${index + 1} bg-white rounded-2xl p-6 relative`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-olive/10">
                    <i className={`${step.icon} text-lg text-brand-olive`}></i>
                  </div>
                  <span className="font-serif text-3xl text-brand-beige font-bold">{step.step}</span>
                </div>
                <h3 className="font-serif text-brand-brown-dark text-lg font-semibold mb-2">
                  {step.title}
                </h3>
                <p className="font-sans text-brand-brown/70 text-sm leading-relaxed">
                  {step.description}
                </p>
                {index < process.length - 1 && (
                  <div className="absolute top-1/2 -right-3 w-6 h-6 items-center justify-center text-brand-olive/30 hidden lg:flex">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 scroll-animate">
            <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-3">
              What We Stand For
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                className={`scroll-animate scroll-animate-delay-${(index % 2) + 1} flex items-start gap-5 bg-brand-cream rounded-2xl p-6`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-olive/10 flex-shrink-0">
                  <i className={`${value.icon} text-xl text-brand-olive`}></i>
                </div>
                <div>
                  <h3 className="font-serif text-brand-brown-dark text-lg font-semibold mb-2">
                    {value.title}
                  </h3>
                  <p className="font-sans text-brand-brown/70 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Inquiry */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-brand-beige-light">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <div className="scroll-animate">
            <span className="inline-block font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium mb-4">
              Get in Touch
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold mb-6">
              We&apos;d Love to Hear from You
            </h2>
            <p className="font-sans text-brand-brown/70 text-sm md:text-base leading-relaxed mb-8">
              Whether you have a question about a product, want to learn more about our process, or just want to reach out — we&apos;re always happy to connect.
            </p>
            <div className="space-y-4">
              <a
                href="https://wa.me/923445299984"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white hover:bg-brand-cream rounded-xl px-5 py-4 transition-colors duration-200 cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#25D366] rounded-full flex-shrink-0">
                  <i className="ri-whatsapp-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="font-sans text-brand-brown-dark text-sm font-medium">WhatsApp</p>
                  <p className="font-sans text-brand-brown/60 text-xs">+92 344 5299984</p>
                </div>
              </a>
              <div className="flex items-center gap-4 bg-white rounded-xl px-5 py-4">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-olive/10 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-2-line text-brand-olive text-lg"></i>
                </div>
                <div>
                  <p className="font-sans text-brand-brown-dark text-sm font-medium">Location</p>
                  <p className="font-sans text-brand-brown/60 text-xs">Islamabad, Pakistan</p>
                </div>
              </div>
            </div>
          </div>

          {/* Inquiry Form */}
          <div className="scroll-animate scroll-animate-delay-1">
            <div className="bg-white rounded-2xl p-7">
              <h3 className="font-serif text-xl text-brand-brown-dark font-semibold mb-6">
                Send an Inquiry
              </h3>
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 flex items-center justify-center mx-auto bg-brand-olive/10 rounded-full mb-4">
                    <i className="ri-check-line text-2xl text-brand-olive"></i>
                  </div>
                  <p className="font-serif text-brand-brown-dark text-lg font-semibold mb-2">Message Sent!</p>
                  <p className="font-sans text-brand-brown/60 text-sm">We&apos;ll get back to you on WhatsApp shortly.</p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="pearl-foods-inquiry"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label className="block font-sans text-xs text-brand-brown/60 mb-1.5 font-medium">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      className="w-full border border-brand-beige bg-brand-cream rounded-lg px-4 py-3 font-sans text-sm text-brand-brown-dark placeholder:text-brand-brown/30 focus:outline-none focus:border-brand-olive transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-brand-brown/60 mb-1.5 font-medium">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full border border-brand-beige bg-brand-cream rounded-lg px-4 py-3 font-sans text-sm text-brand-brown-dark placeholder:text-brand-brown/30 focus:outline-none focus:border-brand-olive transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs text-brand-brown/60 mb-1.5 font-medium">
                      Message{' '}
                      <span className={`ml-1 ${formData.message.length > 500 ? 'text-red-500' : 'text-brand-brown/40'}`}>
                        ({formData.message.length}/500)
                      </span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your inquiry or the products you're interested in..."
                      className="w-full border border-brand-beige bg-brand-cream rounded-lg px-4 py-3 font-sans text-sm text-brand-brown-dark placeholder:text-brand-brown/30 focus:outline-none focus:border-brand-olive transition-colors resize-none"
                    />
                    {formData.message.length > 500 && (
                      <p className="text-red-500 text-xs mt-1 font-sans">Message too long — max 500 characters.</p>
                    )}
                  </div>
                  {error && (
                    <p className="text-red-500 text-xs font-sans">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || formData.message.length > 500}
                    className="w-full bg-brand-olive hover:bg-brand-olive-dark disabled:opacity-50 text-white font-sans font-medium py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? 'Sending...' : 'Send Inquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;